import cn from 'classnames';
import React from 'react';
import { Cell, Row, flexRender } from '@tanstack/react-table';
import { CaretDown, CaretRight } from '@tcl-boron-icons/icons';

import { handleTableRowLinkClick } from '~/components/_tables/_shared/tableRowLink';

import type { CellClickHandler, ExtendCellPropsFn, ExtendRowPropsFn, FilterableDataTableClasses } from './tableTypes';

type FilterableDataTableBodyProps<D extends object> = {
  classNameFor: (slot: keyof FilterableDataTableClasses) => string;
  extendCellProps: ExtendCellPropsFn;
  extendRowProps: ExtendRowPropsFn;
  getRowHref?: (row: D) => string | undefined;
  getRowLinkLabel?: (row: D) => string;
  onCellClick?: CellClickHandler<D>;
  onRowLinkClick?: (row: D) => void;
  pageRows: Row<D>[];
  renderExpandedRow?: (row: Row<D>) => React.ReactNode;
  showExpandedRowByDefault?: boolean;
  setCellWidthAndPadding: (props: { style?: React.CSSProperties }, columnWidth: string | number | undefined) => void;
};

const toggleRowExpandedOnCellClick = <D extends object>(
  _event: React.MouseEvent<HTMLTableCellElement>,
  cell: Cell<D, unknown>,
) => {
  cell.row.toggleExpanded();
};

function FilterableDataTableBody<D extends object>({
  classNameFor,
  extendCellProps,
  extendRowProps,
  getRowHref,
  getRowLinkLabel,
  onCellClick,
  onRowLinkClick,
  pageRows,
  renderExpandedRow,
  setCellWidthAndPadding,
  showExpandedRowByDefault,
}: FilterableDataTableBodyProps<D>) {
  const fallbackCellClick = renderExpandedRow ? toggleRowExpandedOnCellClick : () => undefined;
  const handleCellClick = onCellClick ?? fallbackCellClick;

  const renderExpander = (row: Row<D>) => {
    const Icon = row.getIsExpanded() ? CaretDown : CaretRight;
    const height = row.getIsExpanded() ? 6 : 11;
    return (
      <span className={classNameFor('expander')}>
        <Icon height={height} />
      </span>
    );
  };

  return (
    <tbody className={classNameFor('tbody')}>
      {pageRows.map((row) => {
        const rowHref = getRowHref?.(row.original);
        const baseRowProps = {
          className: cn(classNameFor('tr'), rowHref != null && classNameFor('linkedRow')),
        };
        const rowProps = extendRowProps(baseRowProps, { row });

        return (
          <React.Fragment key={row.id}>
            <tr {...rowProps}>
              {row.getVisibleCells().map((cell, columnIndex) => {
                const meta = cell.column.columnDef.meta;
                const width = meta?.columnWidth;
                const baseCellProps = {
                  className: classNameFor('td'),
                  style: {},
                };

                setCellWidthAndPadding(baseCellProps, width);
                const cellProps = extendCellProps(baseCellProps, { cell });

                return (
                  <td {...cellProps} key={cell.id} onClick={(event) => handleCellClick(event, cell)}>
                    {columnIndex === 0 && rowHref && (
                      <a
                        aria-label={getRowLinkLabel?.(row.original) ?? 'Open row'}
                        className={classNameFor('rowLink')}
                        href={rowHref}
                        onClick={(event) => {
                          if (onRowLinkClick) {
                            handleTableRowLinkClick(event, () => onRowLinkClick(row.original));
                          }
                        }}
                      />
                    )}
                    {renderExpandedRow && !showExpandedRowByDefault && columnIndex === 0 && renderExpander(row)}
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
            {renderExpandedRow && (row.getIsExpanded() || showExpandedRowByDefault) && (
              <tr className={cn(classNameFor('tr'), classNameFor('trExpanded'))}>
                <td
                  colSpan={row.getVisibleCells().length}
                  className={cn(classNameFor('td'), classNameFor('tdExpanded'))}
                >
                  {renderExpandedRow(row)}
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}

export default FilterableDataTableBody;
